import { Cancel, Delete, Edit, Save } from '@mui/icons-material';
import { SxProps, alpha, useTheme } from '@mui/material';
import {
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridValidRowModel,
  MuiEvent,
} from '@mui/x-data-grid-pro';
import { DataGridProProps } from '@mui/x-data-grid-pro/models/dataGridProProps';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirmDialog } from '~/components/DialogsProvider';
import { UseDataGridCRUDValidationOutput } from '~/components/SolaForceDataGrid/hooks/useDataGridCRUDValidation';
import { mergeSxProps } from '~/utils/helpers/sxProps';

export const crudCellClassName = {
  noEditing: 'noEditing',
  invalidCell: 'invalidCell',
};

export type DataGridCRUDConfig<
  R extends GridValidRowModel,
  RId extends string | number,
> = {
  data: {
    onSubmitCreate: (createdRow: R) => Promise<R>;
    onSubmitUpdate: (updatedRow: R) => Promise<R>;
    onSubmitDelete: (deletedRow: R) => Promise<void>;
    onCancelEdit?: (editedRow: R) => void;
  };
  getRowId: (row: R) => RId;
  addNewRow: {
    getNewId: (rows: R[]) => RId;
    getInitialRowData: (newId: RId) => R;
    fieldToFocus: string;
    newRowPosition?: 'start' | 'end';
  };
  isRowEditable: (params: GridRowParams<R>) => boolean;
  enableMultiEdit?: boolean;
  getDeleteRowMessage?: (row: R) => string;
  permissions: {
    canAddNew: boolean;
  };
} & Pick<UseDataGridCRUDValidationOutput<R>, 'actionsValidation'>;

type PropsType<R extends GridValidRowModel, RId extends string | number> = {
  dataGridProps: DataGridProProps<R>;
  config: DataGridCRUDConfig<R, RId>;
};

export type DataGridCRUDOnAddNewRowCallback<R extends GridValidRowModel> = (
  customNewRow?: (defaultRow: R) => R,
) => void;

type UseDataGridCRUDActionsOutput<R extends GridValidRowModel> = {
  toolbarProps: {
    addNew: {
      onAddNewRow: DataGridCRUDOnAddNewRowCallback<R>;
      available: boolean;
      disabled: boolean;
    };
  };
  dataGridProps: Pick<
    DataGridProProps<R>,
    | 'rowModesModel'
    | 'editMode'
    | 'columns'
    | 'rows'
    | 'onRowEditStart'
    | 'onRowEditStop'
    | 'experimentalFeatures'
    | 'processRowUpdate'
    | 'onProcessRowUpdateError'
    | 'sx'
  >;
};

export const useDataGridCRUDActions = <
  R extends GridValidRowModel,
  RId extends string | number,
>(
  props: PropsType<R, RId>,
): UseDataGridCRUDActionsOutput<R> => {
  const {
    dataGridProps,
    config: {
      data: { onSubmitCreate, onSubmitDelete, onSubmitUpdate, onCancelEdit },
      getRowId,
      enableMultiEdit,
      addNewRow,
      isRowEditable,
      getDeleteRowMessage,
      actionsValidation: { syncCRUDErrorState },
      permissions: { canAddNew },
    },
  } = props;

  const { rows: readModeRows, columns } = dataGridProps;

  const { t } = useTranslation();
  const confirm = useConfirmDialog();

  const isNewRow = useCallback(
    (row: R) => {
      const rowId = getRowId(row);

      return !readModeRows.some((r) => getRowId(r) === rowId);
    },
    [readModeRows, getRowId],
  );

  const checkRowId = useCallback(
    (id: RId) => {
      return {
        isMatch: (row: R) => getRowId(row) === id,
        isNotMatch: (row: R) => getRowId(row) !== id,
      };
    },
    [getRowId],
  );

  const [editModeRows, setEditModeRows] = useState<R[]>([]);

  const updateEditModeRows = useCallback(
    (action: 'add' | 'remove') => (row: R) => {
      const { isNotMatch } = checkRowId(getRowId(row));
      const getOtherRows = (rows: R[]) => rows.filter(isNotMatch);

      switch (action) {
        case 'add':
          setEditModeRows((oldRows) => getOtherRows(oldRows).concat(row));
          break;
        case 'remove':
          setEditModeRows((oldRows) => getOtherRows(oldRows));
          break;
      }
    },
    [setEditModeRows, getRowId, checkRowId],
  );
  const removeFromEditModeRows = updateEditModeRows('remove');
  const addToEditModeRows = updateEditModeRows('add');

  const tableRows = useMemo(() => {
    const readModeIds = readModeRows.map((r) => getRowId(r));
    const newRows = editModeRows.filter(
      (r) => !readModeIds.includes(getRowId(r)),
    );

    const existingRows = readModeRows.map((readModeRow) => {
      const rowId = getRowId(readModeRow);
      const editModeRow = editModeRows.find((r) => getRowId(r) === rowId);

      return !!editModeRow ? editModeRow : readModeRow;
    });

    return addNewRow.newRowPosition === 'end'
      ? existingRows.concat(newRows)
      : newRows.concat(existingRows);
  }, [getRowId, readModeRows, editModeRows, addNewRow.newRowPosition]);

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const { isRowEditDisabled } = useMemo(() => {
    const isEditingRow = Object.values(rowModesModel).some(
      (model) => model.mode === GridRowModes.Edit,
    );

    return {
      isRowEditDisabled: !enableMultiEdit && isEditingRow,
    };
  }, [enableMultiEdit, rowModesModel]);

  const onRowEditStart = (
    params: GridRowParams,
    event: MuiEvent<React.SyntheticEvent>,
  ) => {
    event.defaultMuiPrevented = true;
  };

  const onRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  // Actions
  const onEditAction = useCallback(
    (id: RId) => () => {
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit },
      }));

      const { isMatch } = checkRowId(id);
      const editRow = tableRows.find(isMatch);

      if (!!editRow) {
        addToEditModeRows(editRow);
      }
    },
    [setRowModesModel, tableRows, addToEditModeRows, checkRowId],
  );

  const onSaveAction = useCallback(
    (id: RId) => () => {
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.View },
      }));
    },
    [setRowModesModel],
  );

  const onDeleteAction = useCallback(
    (id: RId) => () => {
      const { isMatch } = checkRowId(id);
      const deleteRow = tableRows.find(isMatch);

      if (!!deleteRow) {
        // Remove unsubmitted new row
        if (isNewRow(deleteRow)) {
          removeFromEditModeRows(deleteRow);
          return;
        }

        // Remove existing data row
        const deleteExisting = async () => {
          return onSubmitDelete(deleteRow).then(() => {
            removeFromEditModeRows(deleteRow);
          });
        };

        if (!!getDeleteRowMessage) {
          return confirm(getDeleteRowMessage(deleteRow), (confirmed) => {
            if (confirmed) {
              return deleteExisting();
            }
          });
        } else {
          return deleteExisting();
        }
      }
    },
    [
      removeFromEditModeRows,
      tableRows,
      checkRowId,
      confirm,
      getDeleteRowMessage,
      onSubmitDelete,
      isNewRow,
    ],
  );

  const onCancelAction = useCallback(
    (id: RId) => () => {
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));

      const { isMatch } = checkRowId(id);
      const editedRow = editModeRows.find(isMatch);
      if (!!editedRow) {
        if (!!onCancelEdit) {
          onCancelEdit(editedRow);
        }
        removeFromEditModeRows(editedRow);
        if (!!syncCRUDErrorState) {
          syncCRUDErrorState([], id);
        }
      }
    },
    [
      setRowModesModel,
      removeFromEditModeRows,
      editModeRows,
      checkRowId,
      onCancelEdit,
      syncCRUDErrorState,
    ],
  );

  const handleAddNew = useCallback(
    (customNewRow?: (defaultRow: R) => R) => {
      const { getNewId, getInitialRowData, fieldToFocus } = addNewRow;

      const newId = getNewId(tableRows);
      const newRowData = getInitialRowData(newId);
      addToEditModeRows(!!customNewRow ? customNewRow(newRowData) : newRowData);
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [newId]: { mode: GridRowModes.Edit, fieldToFocus },
      }));
    },
    [addNewRow, tableRows, addToEditModeRows, setRowModesModel],
  );

  // Process update
  const processRowUpdate: DataGridProProps<R>['processRowUpdate'] = useCallback(
    async (newRow: R) => {
      const rowId = getRowId(newRow);

      return (
        isNewRow(newRow) ? onSubmitCreate(newRow) : onSubmitUpdate(newRow)
      )
        .then((result) => {
          removeFromEditModeRows(newRow);
          return Promise.resolve(result);
        })
        .catch((err) => {
          // Keep row in edit mode
          setRowModesModel((oldModel) => ({
            ...oldModel,
            [rowId]: { mode: GridRowModes.Edit },
          }));

          return Promise.reject(err);
        });
    },
    [
      onSubmitCreate,
      onSubmitUpdate,
      removeFromEditModeRows,
      setRowModesModel,
      getRowId,
      isNewRow,
    ],
  );

  const onProcessRowUpdateError: DataGridProProps<R>['onProcessRowUpdateError'] =
    useCallback(
      (error) => {
        if (dataGridProps?.onProcessRowUpdateError) {
          dataGridProps.onProcessRowUpdateError(error);
        }
      },
      [dataGridProps],
    );

  // Column configs
  const columnsWithActions: GridColDef<R>[] = useMemo(() => {
    return columns.map((column) => {
      if (column.type === 'actions') {
        return {
          ...column,
          getActions: (params: GridRowParams<R>) => {
            const { row } = params;
            const id = getRowId(row);
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

            if (!isRowEditable(params)) {
              return [];
            }

            if (isInEditMode) {
              return [
                <GridActionsCellItem
                  icon={<Save />}
                  label={t('save')}
                  title={t('save')}
                  onClick={onSaveAction(id)}
                  color={'inherit'}
                />,
                <GridActionsCellItem
                  icon={<Cancel />}
                  label={t('cancel')}
                  title={t('cancel')}
                  className="textPrimary"
                  onClick={onCancelAction(id)}
                  color={'inherit'}
                />,
              ];
            }

            return [
              <GridActionsCellItem
                icon={<Edit />}
                label={t('edit')}
                title={t('edit')}
                className="textPrimary"
                onClick={onEditAction(id)}
                color={'inherit'}
                disabled={isRowEditDisabled}
              />,
              <GridActionsCellItem
                icon={<Delete />}
                label={t('delete')}
                title={t('delete')}
                onClick={onDeleteAction(id)}
                color={'inherit'}
                disabled={isRowEditDisabled}
              />,
            ];
          },
        };
      }
      return column;
    });
  }, [
    t,
    columns,
    rowModesModel,
    getRowId,
    onEditAction,
    onSaveAction,
    onCancelAction,
    onDeleteAction,
    isRowEditDisabled,
    isRowEditable,
  ]);

  const theme = useTheme();

  const getEditCellStyle = (backgroundColor: string, outlineColor: string) => {
    return {
      '&.MuiDataGrid-cell': {
        backgroundColor: alpha(backgroundColor, 0.2),
        '&:focus-within': {
          outline: `${outlineColor} solid 1px`,
          backgroundColor: alpha(backgroundColor, 0.1),
        },
      },
    };
  };

  const crudSxProps: SxProps = {
    [`& .MuiDataGrid-cell--editing:not(.${crudCellClassName.noEditing})`]: {
      ...getEditCellStyle(
        theme.palette.success.light,
        theme.palette.success.main,
      ),
      [`&.${crudCellClassName.invalidCell}`]: {
        ...getEditCellStyle(
          theme.palette.error.light,
          theme.palette.error.main,
        ),
      },
    },
  };

  return {
    toolbarProps: {
      addNew: {
        onAddNewRow: handleAddNew,
        available: canAddNew,
        disabled: isRowEditDisabled,
      },
    },
    dataGridProps: {
      ...dataGridProps,
      rows: tableRows,
      columns: columnsWithActions,
      editMode: 'row',
      rowModesModel,
      onRowEditStart,
      onRowEditStop,
      experimentalFeatures: {
        ...dataGridProps?.experimentalFeatures,
      },
      processRowUpdate,
      onProcessRowUpdateError,
      sx: mergeSxProps(crudSxProps, dataGridProps.sx),
    },
  };
};
